import { Form, Formik } from 'formik'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { PatientHistoryType } from 'src/graphql.generated'
import { Alert, Box, Typography } from '@mui/material'
import { Loader } from 'src/designSystem'
import { DraftIndicator } from 'src/designSystem/DraftIndicator'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import checkIfAutofillEnabled from 'src/participantHistory/checkIfAutofillEnabled'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import { useGetPatientCredentialingHealthPlanQuery } from './getPatientCredentialingHealthPlan.generated'
import { CHECK_IN_CALL_NOTE_SCHEMA, CheckinCallNoteType } from './CheckinCallNoteSchema'
import FormPersistor from '../FormPersistor'
import SubmitButton from '../SubmitButton'
import { CHECK_IN_CALL_OUTSIDE_DASHBOARD, CheckinCallNoteFields } from './CheckinCallNoteFields'
import { useLocalStorageWithPrefillValues } from '../useLocalStorageWithPrefillValues'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import AutofillCheckinCallNoteButton from './AutofillCheckinCallNoteButton'

export const LINKED_CALL_REQUIRED_ERROR =
  'Select the check-in call this note is for, or mark it as done outside of the dashboard.'

type Props = {
  formType: PatientHistoryType.CheckInCall
  uid: string
  afterSubmittedCallback?: () => void
}

const getDefaultValues = () => ({
  date: '',
  FIRST_NAME: '',
  LAST_NAME: '',
  MEMBER_ID: '',
  PROVIDER_NAME: '',
  LICENSE_TYPE: '',
  CALL_START_TIME: '',
  CALL_END_TIME: '',
  CALL_STATUS: 'CALL_HAD' as const,
  DESCRIPTION: '',
  SERVICE_DELIVERY_AND_COMPLIANCE: {
    CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH: true,
    CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE: true,
    CONFIRM_CALL_TIME_AND_LOCATION: true,
  },
  CALL_METHOD: 'IN_APP' as const,
  SMART_GOALS: '',
  BILLING_CODES: [],
  REASON_FOR_CALL: '',
  INTERVENTIONS: {
    COGNITIVE_BEHAVIORAL_THERAPY_TECHNIQUES: false,
    COGNITIVE_RESTRUCTURING: false,
    COPING_SKILLS_DEVELOPMENT: false,
    RELAPSE_PREVENTION_PLANNING: false,
    MINDFULNESS_TECHNIQUES: false,
    COMMUNICATION_SKILLS_TRAINING: false,
    SOLUTION_FOCUSED_BRIEF_THERAPY_TECHNIQUES: false,
    CRISIS_INTERVENTION: false,
    MOTIVATIONAL_INTERVIEWING_TECHNIQUES: false,
    RESOURCE_LINKAGE: false,
    OTHER: '',
  },
  RISK_LEVEL_HAS_CHANGE: 'no' as const,
  PROTECTIVE_FACTORS_IDENTIFIED: {
    STRONG_SOCIAL_SUPPORT: false,
    ENGAGEMENT_IN_THERAPY: false,
    COPING_SKILLS: false,
    FUTURE_ORIENTED_THINKING: false,
    MOTIVATED_FOR_TREATMENT: false,
    RELIGIOUS_BELIEFS: false,
    OTHER: '',
  },
  NEXT_STEPS: {
    SAFETY_PLAN_REVIEWED_EMAILED: false,
    SAFETY_PLAN_DOCUMENTED_IN_RECORD: false,
    NO_FURTHER_ASSISTANCE_NEEDED: false,
    LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED: false,
    OTHER: '',
  },
  PARTICIPANT_RESPONSE: {
    ENGAGED_AND_SHOWED_UNDERSTANDING_OF_TECHNIQUES: false,
    ACTIVE_IN_PROBLEM_SOLVING: false,
    HIGHLIGHTED_PERSONAL_STRENGTH_AND_RESOURCES: false,
    PRACTICED_AND_BUILT_CONFIDENCE: false,
    EXPRESSED_MOTIVATION_TO_APPLY_TECHNIQUES: false,
    OTHER: '',
  },
  PLAN: {
    CONTINUE_SUPPORT_VIA_APP_CHAT: false,
    OFFER_CHECK_IN_CALLS_AS_NEEDED: false,
    DISENROLL_PARTICIPANT_AT_REQUEST: false,
    SEND_RESOURCES_AND_INFORMATION: false,
    OTHER: '',
  },
})

const FORM_TYPE = PatientHistoryType.CheckInCall

export function CheckinCallNote({ formType, uid, afterSubmittedCallback }: Props) {
  const location = useLocation()
  const urlCallID = useMemo(() => new URLSearchParams(location.search).get('callID'), [location.search])
  const [selectedCallID, setSelectedCallID] = useState<string | null>(urlCallID)
  const [selectedCallError, setSelectedCallError] = useState<string | null>(null)

  useEffect(() => {
    setSelectedCallID(urlCallID)
    setSelectedCallError(null)
  }, [urlCallID])

  const handleSelectedCallIDChange = useCallback((callID: string | null) => {
    setSelectedCallID(callID)
    setSelectedCallError(null)
  }, [])

  const getDefaults = useCallback(() => getDefaultValues(), [])
  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, formType, getDefaults, urlCallID)
  const { data } = useGetPatientCredentialingHealthPlanQuery({ variables: { uid } })
  const isKaiser = Boolean(checkIfKaiserHealthPlan(data?.patient?.credentialingHealthPlan))
  const memberIdLabel = isKaiser ? 'Kaiser MRN' : 'Member ID'

  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  if (status === 'loading') {
    return <Loader size="large" />
  }

  return (
    <Formik<CheckinCallNoteType>
      validationSchema={toFormikValidationSchema(CHECK_IN_CALL_NOTE_SCHEMA)}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        if (!selectedCallID) {
          setSelectedCallError(LINKED_CALL_REQUIRED_ERROR)
          return
        }
        await addPatientHistoryEntry({
          uid,
          date: values.date,
          type: formType,
          value: values,
          ...(selectedCallID !== CHECK_IN_CALL_OUTSIDE_DASHBOARD ? { references: { callID: selectedCallID } } : {}),
        })
        resetForm({ values: getDefaultValues() })
        clearFormFromLocalStorage(uid, formType)
        setSelectedCallID(null)
        afterSubmittedCallback?.()
      }}
    >
      {({ isSubmitting, errors, dirty }) => (
        <Form>
          {checkIfAutofillEnabled() && (
            <AutofillCheckinCallNoteButton
              uid={uid}
              getDefaultValues={getDefaultValues}
              selectedCallID={selectedCallID}
              onSelectedCallIDChange={handleSelectedCallIDChange}
              sx={{ mt: 2, mb: 2 }}
            />
          )}

          {(status === 'draft' || dirty) && <DraftIndicator dirty={dirty} />}
          {status === 'error' && (
            <Box mt={3}>
              <Alert severity="error">Failed to load form prefill data.</Alert>
            </Box>
          )}

          <CheckinCallNoteFields
            uid={uid}
            memberIdLabel={memberIdLabel}
            selectedCallID={selectedCallID}
            onSelectedCallIDChange={handleSelectedCallIDChange}
            selectedCallError={selectedCallError}
          />

          <Box mt={5}>
            <SubmitButton isSubmitting={isSubmitting} />
          </Box>

          {Object.values(errors).filter((error) => error).length > 0 && (
            <Box mt={3}>
              <Typography variant="body1" color="error">
                Fill in required fields.
              </Typography>
            </Box>
          )}

          <FormPersistor uid={uid} formType={FORM_TYPE} />
        </Form>
      )}
    </Formik>
  )
}
